import React from 'react';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { 
  Box, 
  Typography, 
  Chip, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { makeStyles } from '@material-ui/core/styles';
import { useAsync } from 'react-use';
import { belongsToSystem } from '../utils/entityHelpers';

const useStyles = makeStyles(theme => ({
  entityItem: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  systemEntity: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontWeight: 600,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  childEntity: {
    padding: theme.spacing(1.5, 0, 1.5, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  entityInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  entityName: {
    fontWeight: 500,
  },
  kindChip: {
    marginLeft: theme.spacing(1),
  },
  kindSeparator: {
    padding: theme.spacing(1.5, 0, 0.5, 0),
    marginTop: theme.spacing(1),
    borderTop: `2px solid ${theme.palette.divider}`,
    '&:first-child': {
      marginTop: 0,
      borderTop: 'none',
    },
  },
  kindLabel: {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
  },
  typeChip: {
    marginLeft: theme.spacing(0.5),
  },
  viewAllButton: {
    marginTop: theme.spacing(2),
  },
  dialogContent: {
    minWidth: '600px',
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

interface MyOwnedEntitiesCardProps {
  /** Maximum number of systems to display (default: 3) */
  systemLimit?: number;
  /** Maximum number of components to display (default: 5) */
  componentLimit?: number;
}

export const MyOwnedEntitiesCard = ({ 
  systemLimit = 3,
  componentLimit = 5,
}: MyOwnedEntitiesCardProps = {}) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [expandedSystems, setExpandedSystems] = React.useState<Set<string>>(new Set());

  const { value: entities, loading, error } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    const userRef = identity.userEntityRef;
    const ownershipRefs = identity.ownershipEntityRefs || [];

    // Build filter to include entities owned by user OR any of their groups
    const ownerFilters = [userRef, ...ownershipRefs];

    const { items } = await catalogApi.getEntities({
      filter: {
        'relations.ownedBy': ownerFilters,
      },
    });

    // Group entities hierarchically: systems with their components
    const systems = items.filter(e => e.kind.toLowerCase() === 'system');
    const nonSystems = items.filter(e => e.kind.toLowerCase() !== 'system');
    
    // Sort systems alphabetically
    systems.sort((a, b) => 
      (a.metadata.name || '').localeCompare(b.metadata.name || '')
    );
    
    // Build hierarchical structure
    const hierarchicalEntities: Entity[] = [];
    
    systems.forEach(system => {
      // Add the system
      hierarchicalEntities.push(system);
      
      // Find and add components that belong to this system
      const systemComponents = nonSystems
        .filter(entity => {
          const systemRef = (entity.spec?.system as string) || '';
          return belongsToSystem(systemRef, system.metadata.name);
        })
        .sort((a, b) => 
          (a.metadata.name || '').localeCompare(b.metadata.name || '')
        );
      
      hierarchicalEntities.push(...systemComponents);
    });
    
    // Add orphaned entities (not part of any system) at the end
    const orphanedEntities = nonSystems
      .filter(entity => {
        const systemRef = (entity.spec?.system as string) || '';
        if (!systemRef) return true;
        
        // Check if the referenced system is in our owned systems
        return !systems.some(s => belongsToSystem(systemRef, s.metadata.name));
      })
      .sort((a, b) => 
        (a.metadata.name || '').localeCompare(b.metadata.name || '')
      );
    
    hierarchicalEntities.push(...orphanedEntities);

    return hierarchicalEntities;
  }, []);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const toggleSystem = (systemName: string) => {
    setExpandedSystems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(systemName)) {
        newSet.delete(systemName);
      } else {
        newSet.add(systemName);
      }
      return newSet;
    });
  };

  // Helper function to format entity name without 'default/' namespace
  const formatEntityName = (entity: Entity) => {
    const namespace = entity.metadata.namespace || 'default';
    const name = entity.metadata.name || '';
    
    // Only remove 'default/' prefix, keep other namespaces
    if (namespace === 'default') {
      return name;
    }
    return `${namespace}/${name}`;
  };

  if (loading) {
    return (
      <InfoCard title="My Owned Entities">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="My Owned Entities">
        <Typography color="error">
          Failed to load owned entities: {error.message}
        </Typography>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="My Owned Entities" noPadding>
      {entities && entities.length > 0 ? (
        <Box p={2}>
          {(() => {
            const renderedItems: JSX.Element[] = [];
            let i = 0;
            let systemsRendered = 0;
            let componentsRendered = 0;
            
            while (i < entities.length && (systemsRendered < systemLimit || componentsRendered < componentLimit)) {
              const entity = entities[i];
              const isSystem = entity.kind.toLowerCase() === 'system';
              
              if (isSystem && systemsRendered < systemLimit) {
                // Count children for this system
                const systemName = entity.metadata.name || '';
                const children: Entity[] = [];
                let j = i + 1;
                
                while (j < entities.length) {
                  const nextEntity = entities[j];
                  const nextSystemRef = (nextEntity.spec?.system as string) || '';
                  
                  if (belongsToSystem(nextSystemRef, systemName)) {
                    children.push(nextEntity);
                    j++;
                  } else {
                    break;
                  }
                }
                
                const isExpanded = expandedSystems.has(systemName);
                
                // Render system with expand button
                renderedItems.push(
                  <div key={`system-${i}`} className={classes.systemEntity} onClick={() => toggleSystem(systemName)}>
                    <div className={classes.entityInfo} style={{ flex: 1 }}>
                      <div>
                        <EntityRefLink
                          entityRef={entity}
                          className={classes.entityName}
                        />
                        <Chip
                          label={entity.kind}
                          size="small"
                          className={classes.kindChip}
                          color="primary"
                        />
                        {entity.spec?.type && (
                          <Chip
                            label={entity.spec.type as string}
                            size="small"
                            variant="outlined"
                            className={classes.typeChip}
                            style={{ 
                              borderColor: '#4caf50', 
                              color: '#4caf50' 
                            }}
                          />
                        )}
                        {children.length > 0 && (
                          <Chip
                            label={`${children.length} item${children.length !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                            style={{ marginLeft: '8px' }}
                          />
                        )}
                      </div>
                      {children.length > 0 && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleSystem(systemName); }}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </div>
                    {entity.metadata?.description && (
                      <Typography variant="body2" color="textSecondary">
                        {entity.metadata.description}
                      </Typography>
                    )}
                  </div>
                );
                
                // Render children in Collapse
                if (children.length > 0) {
                  renderedItems.push(
                    <Collapse key={`collapse-${i}`} in={isExpanded} timeout="auto" unmountOnExit>
                      {children.map((childEntity, childIndex) => (
                        <div key={`child-${i}-${childIndex}`} className={classes.childEntity}>
                          <div className={classes.entityInfo}>
                            <div>
                              <EntityRefLink
                                entityRef={childEntity}
                                className={classes.entityName}
                              />
                              <Chip
                                label={childEntity.kind}
                                size="small"
                                className={classes.kindChip}
                                color="primary"
                              />
                              {childEntity.spec?.type && (
                                <Chip
                                  label={childEntity.spec.type as string}
                                  size="small"
                                  variant="outlined"
                                  className={classes.typeChip}
                                  style={{ 
                                    borderColor: '#4caf50', 
                                    color: '#4caf50' 
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          {childEntity.metadata?.description && (
                            <Typography variant="body2" color="textSecondary">
                              {childEntity.metadata.description}
                            </Typography>
                          )}
                        </div>
                      ))}
                    </Collapse>
                  );
                }
                
                systemsRendered++;
                i = j; // Skip past the children we just rendered
              } else if (!isSystem && componentsRendered < componentLimit) {
                // Orphaned entity (not part of a system)
                renderedItems.push(
                  <div key={`orphan-${i}`} className={classes.entityItem}>
                    <div className={classes.entityInfo}>
                      <div>
                        <EntityRefLink
                          entityRef={entity}
                          className={classes.entityName}
                        />
                        <Chip
                          label={entity.kind}
                          size="small"
                          className={classes.kindChip}
                          color="primary"
                        />
                        {entity.spec?.type && (
                          <Chip
                            label={entity.spec.type as string}
                            size="small"
                            variant="outlined"
                            className={classes.typeChip}
                            style={{ 
                              borderColor: '#4caf50', 
                              color: '#4caf50' 
                            }}
                          />
                        )}
                      </div>
                    </div>
                    {entity.metadata?.description && (
                      <Typography variant="body2" color="textSecondary">
                        {entity.metadata.description}
                      </Typography>
                    )}
                  </div>
                );
                componentsRendered++;
                i++;
              } else {
                // Skip entities that exceed limits
                i++;
              }
            }
            
            return renderedItems;
          })()}
          {(() => {
            // Count total number of systems for the "View All" button
            const systemCount = entities.filter(e => e.kind.toLowerCase() === 'system').length;
            const orphanedCount = entities.filter(e => {
              const isSystem = e.kind.toLowerCase() === 'system';
              const systemRef = (e.spec?.system as string) || '';
              return !isSystem && !systemRef;
            }).length;
            const totalTopLevel = systemCount + orphanedCount;
            
            return totalTopLevel > 10 ? (
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                className={classes.viewAllButton}
                onClick={handleOpenDialog}
              >
                View All ({entities.length} owned entities)
              </Button>
            ) : null;
          })()}
        </Box>
      ) : (
        <div className={classes.emptyState}>
          <Typography>No owned entities found.</Typography>
          <Typography variant="body2">
            Entities owned by you or your groups will appear here.
          </Typography>
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>All Owned Entities</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <List disablePadding>
            {entities && (() => {
              const renderedItems: JSX.Element[] = [];
              let i = 0;
              
              while (i < entities.length) {
                const entity = entities[i];
                const isSystem = entity.kind.toLowerCase() === 'system';
                
                if (isSystem) {
                  // Count children for this system
                  const systemName = entity.metadata.name || '';
                  const children: Entity[] = [];
                  let j = i + 1;
                  
                  while (j < entities.length) {
                    const nextEntity = entities[j];
                    const nextSystemRef = (nextEntity.spec?.system as string) || '';
                    
                    if (belongsToSystem(nextSystemRef, systemName)) {
                      children.push(nextEntity);
                      j++;
                    } else {
                      break;
                    }
                  }
                  
                  const isExpanded = expandedSystems.has(systemName);
                  
                  // Render system with expand button
                  renderedItems.push(
                    <ListItem
                      key={`modal-system-${i}`}
                      button
                      onClick={() => toggleSystem(systemName)}
                      className={classes.listItem}
                      style={{ fontWeight: 600 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1">
                            <Chip 
                              label={entity.kind.toLowerCase()} 
                              size="small" 
                              color="primary"
                              style={{ marginRight: '8px' }}
                            />
                            {entity.spec?.type && (
                              <Chip 
                                label={String(entity.spec.type)} 
                                size="small" 
                                variant="outlined"
                                style={{ 
                                  marginRight: '8px',
                                  borderColor: '#4caf50',
                                  color: '#4caf50'
                                }}
                              />
                            )}
                            {formatEntityName(entity)}
                            {children.length > 0 && (
                              <Chip
                                label={`${children.length} item${children.length !== 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                                style={{ marginLeft: '8px' }}
                              />
                            )}
                          </Typography>
                        }
                        secondary={entity.metadata.description}
                      />
                      {children.length > 0 && (
                        <IconButton size="small" edge="end">
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </ListItem>
                  );
                  
                  // Render children in Collapse
                  if (children.length > 0) {
                    renderedItems.push(
                      <Collapse key={`modal-collapse-${i}`} in={isExpanded} timeout="auto" unmountOnExit>
                        {children.map((childEntity, childIndex) => (
                          <ListItem
                            key={`modal-child-${i}-${childIndex}`}
                            button
                            component="a"
                            href={`/catalog/${childEntity.metadata.namespace || 'default'}/${
                              childEntity.kind
                            }/${childEntity.metadata.name}`}
                            className={classes.listItem}
                            style={{
                              paddingLeft: '48px',
                              borderLeft: '3px solid #1976d2',
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography variant="body1">
                                  <Chip 
                                    label={childEntity.kind.toLowerCase()} 
                                    size="small" 
                                    color="primary"
                                    style={{ marginRight: '8px' }}
                                  />
                                  {childEntity.spec?.type && (
                                    <Chip 
                                      label={String(childEntity.spec.type)} 
                                      size="small" 
                                      variant="outlined"
                                      style={{ 
                                        marginRight: '8px',
                                        borderColor: '#4caf50',
                                        color: '#4caf50'
                                      }}
                                    />
                                  )}
                                  {formatEntityName(childEntity)}
                                </Typography>
                              }
                              secondary={childEntity.metadata.description}
                            />
                          </ListItem>
                        ))}
                      </Collapse>
                    );
                  }
                  
                  i = j; // Skip past the children
                } else {
                  // Orphaned entity (not part of a system)
                  renderedItems.push(
                    <ListItem
                      key={`modal-orphan-${i}`}
                      button
                      component="a"
                      href={`/catalog/${entity.metadata.namespace || 'default'}/${
                        entity.kind
                      }/${entity.metadata.name}`}
                      className={classes.listItem}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1">
                            <Chip 
                              label={entity.kind.toLowerCase()} 
                              size="small" 
                              color="primary"
                              style={{ marginRight: '8px' }}
                            />
                            {entity.spec?.type && (
                              <Chip 
                                label={String(entity.spec.type)} 
                                size="small" 
                                variant="outlined"
                                style={{ 
                                  marginRight: '8px',
                                  borderColor: '#4caf50',
                                  color: '#4caf50'
                                }}
                              />
                            )}
                            {formatEntityName(entity)}
                          </Typography>
                        }
                        secondary={entity.metadata.description}
                      />
                    </ListItem>
                  );
                  i++;
                }
              }
              
              return renderedItems;
            })()}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </InfoCard>
  );
};
